import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  Optional,
  Self,
  SimpleChanges,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import moment from 'moment-timezone';

import { I18nService } from '@recruitee/i18n';
import { DATE_FORMAT, getDefaultTimezone } from '@recruitee/time';

import { RtInput } from '../../utils/rt-input';
import { segmentedButtonType } from './../../common/segmented-control/segmented-control-button/segmented-control-button.component';
import { DatePickerDirection } from './../date-picker/date-picker.component';

export type TimeData = {
  date: string; // 'YYYY-MM-DD'
  time: string; // 'HH:mm'
  zone: string; // 'Europe/Warsaw'
};

@Component({
  selector: 'rt-time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(focus)': 'focus.emit($event)',
    '(blur)': 'blur.emit($event)',
  },
})
export class TimePickerComponent extends RtInput<TimeData | null> implements OnChanges {
  @Input() public defaultDate: string = moment().format(DATE_FORMAT);
  @Input() public defaultTime: string = '12:00';
  // f.e. "Europe/Warsaw"
  @Input() public defaultTimeZoneName: string = getDefaultTimezone();
  @Input() public showDoneButton: boolean = true;
  @Input() public direction: DatePickerDirection = 'any';

  @Input() public timeInterval: number = 0;

  public activeTab: 'date' | 'time' = 'date';
  public showTimeZonePicker: boolean = false;
  public tmpValue: TimeData | null;

  public segmentedButtons: Array<segmentedButtonType<'date' | 'time'>>;

  constructor(
    private i18n: I18nService,
    cdRef: ChangeDetectorRef,
    @Optional() @Self() ngControl?: NgControl,
  ) {
    super(cdRef, ngControl);
    this.setTmpValue(this.value, false);
    this.setSegmentedButtons();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.setTmpValue({
      date: this.defaultDate,
      time: this.defaultTime,
      zone: this.defaultTimeZoneName,
    });
  }

  public handleDateChange(date: string | string[]): void {
    if (date) {
      this.tmpValue = <TimeData>{
        ...this.tmpValue,
        date,
      };
      this.cdRef.detectChanges();
    }
  }

  public handleTimeChange(time: string): void {
    this.tmpValue = <TimeData>{
      ...this.tmpValue,
      time,
    };
    this.cdRef.detectChanges();
  }

  public handleTimeZoneChange(zone: string): void {
    this.tmpValue = <TimeData>{
      ...this.tmpValue,
      zone,
    };
    this.cdRef.detectChanges();
  }

  public applyChanges(): void {
    this.handleChange(this.tmpValue);
    this.cdRef.detectChanges();
  }

  public switchTab(tab: 'date' | 'time'): void {
    this.activeTab = tab;
    this.setSegmentedButtons();
    this.cdRef.detectChanges();
  }

  protected setValue(value: TimeData | null): void {
    this.value = value;
    this.setTmpValue(value);
  }

  private setTmpValue(value: TimeData | null, runDetectChanges: boolean = false): void {
    const time = (value && value.time) || this.defaultTime;
    this.tmpValue = {
      date: (value && value.date) || this.defaultDate,
      time: moment(time, 'HH:mm').format('LT'),
      zone: (value && value.zone) || this.defaultTimeZoneName,
    };
    if (runDetectChanges) {
      this.cdRef.detectChanges();
    }
  }

  private setSegmentedButtons(): void {
    this.segmentedButtons = [
      {
        isActive: this.activeTab === 'date',
        value: 'date',
        label: this.i18n.translate('rtu.common.date'),
        isAvailable: true,
      },
      {
        isActive: this.activeTab === 'time',
        value: 'time',
        label: this.i18n.translate('rtu.common.time'),
        isAvailable: true,
      },
    ];
  }
}
